import React, { useEffect, useMemo, useState } from 'react';
import {
    CURRENCIES,
    EXCHANGE_RATES,
    ITEM_TYPES,
    PURPOSE_TYPES,
} from '../constants';
import { api } from '../services/api';
import LegoImage from './LegoImage';

function convertCurrency(price, fromCode, toCode, rates) {
    const oldRate = rates[fromCode] || 1;
    const newRate = rates[toCode] || 1;
    return (price / oldRate) * newRate;
}

function formatCurrency(value, code) {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: code,
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 2,
    }).format(value || 0);
}

function StockRecords({ previousFilter, onFilterChange }) {
    const [items, setItems] = useState([]);
    const [exchangeRates, setExchangeRates] = useState(EXCHANGE_RATES);
    const [filterText, setFilterText] = useState(previousFilter.text || '');
    const [displayCurrency, setDisplayCurrency] = useState('USD');
    const [sort, setSort] = useState({
        field: 'legoId',
        direction: 'asc',
        type: 'string',
    });

    useEffect(() => {
        api.getItems()
            .then(setItems)
            .catch((error) => window.alert(error.message));
        api.getExchangeRates()
            .then(setExchangeRates)
            .catch(() => { /* keep hardcoded fallback */ });
    }, []);

    useEffect(() => {
        onFilterChange({ text: filterText, year: '' });
    }, [filterText, onFilterChange]);

    const stocks = useMemo(() => {
        const byLego = new Map();

        items.forEach((item) => {
            if (!byLego.has(item.legoId)) {
                byLego.set(item.legoId, {
                    legoId: item.legoId,
                    displayName: item.displayName,
                    buy: 0,
                    sell: 0,
                    stock: 0,
                    stockValue: 0,
                    profit: 0,
                    rawItems: [],
                });
            }
            const row = byLego.get(item.legoId);
            const fromCode = CURRENCIES[item.currency] || 'USD';
            row.rawItems.push({
                ...item,
                unitPrice: convertCurrency(
                    item.unitPrice,
                    fromCode,
                    displayCurrency,
                    exchangeRates,
                ),
            });
        });

        const result = Array.from(byLego.values())
            .map((row) => {
                const buyItems = row.rawItems.filter(
                    (item) =>
                        item.type === ITEM_TYPES.Buy &&
                        item.purpose !== PURPOSE_TYPES.ForPlay,
                );
                const sellItems = row.rawItems.filter(
                    (item) => item.type === ITEM_TYPES.Sell,
                );

                row.buy = buyItems.reduce(
                    (count, item) => count + item.quantity,
                    0,
                );
                row.sell = sellItems.reduce(
                    (count, item) => count + item.quantity,
                    0,
                );
                row.stock = row.buy - row.sell;

                const totalCost = buyItems.reduce(
                    (sum, item) => sum + item.quantity * item.unitPrice,
                    0,
                );
                row.stockValue = row.buy
                    ? row.stock * (totalCost / row.buy)
                    : 0;
                row.profit =
                    sellItems.reduce(
                        (sum, item) =>
                            sum +
                            item.quantity *
                                item.unitPrice *
                                (1 - item.fees / 100),
                        0,
                    ) - totalCost;

                return row;
            })
            .filter((item) => {
                const term = filterText.toLowerCase();
                return (
                    (item.displayName || '').toLowerCase().includes(term) ||
                    (item.legoId || '').toLowerCase().includes(term)
                );
            })
            .sort((a, b) => {
                let resultValue = 0;
                if (sort.type === 'number') {
                    resultValue = Number(a[sort.field]) - Number(b[sort.field]);
                } else {
                    resultValue = String(a[sort.field] || '').localeCompare(
                        String(b[sort.field] || ''),
                        'zh',
                        {
                            numeric: true,
                            sensitivity: 'base',
                        },
                    );
                }
                return sort.direction === 'asc' ? resultValue : -resultValue;
            });

        return result;
    }, [items, filterText, displayCurrency, sort, exchangeRates]);

    const totalProfit = useMemo(
        () => stocks.reduce((sum, item) => sum + (item.profit || 0), 0),
        [stocks],
    );

    function onSort(field, valueType = 'string') {
        setSort((prev) => ({
            field,
            type: valueType,
            direction:
                prev.field === field && prev.direction === 'asc'
                    ? 'desc'
                    : 'asc',
        }));
    }

    return (
        <div>
            <div className='toolbar'>
                <div className='currency-group'>
                    {['USD', 'CNY', 'EUR'].map((code) => (
                        <button
                            key={code}
                            type='button'
                            className={
                                displayCurrency === code
                                    ? 'chip active'
                                    : 'chip'
                            }
                            onClick={() => setDisplayCurrency(code)}
                        >
                            {code === 'USD' ? '$' : code === 'CNY' ? '¥' : '€'}
                        </button>
                    ))}
                </div>
                <div className='total-text'>
                    Total profit: {formatCurrency(totalProfit, displayCurrency)}
                </div>
                <div className='toolbar-filters'>
                    <input
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        placeholder='Filter...'
                    />
                </div>
            </div>
            <div className='table-wrap'>
                <table className='stock-table'>
                    <thead>
                        <tr>
                            <th>Image</th>
                            <th onClick={() => onSort('legoId')}>LegoId</th>
                            <th onClick={() => onSort('displayName')}>Name</th>
                            <th onClick={() => onSort('buy', 'number')}>
                                For sale
                            </th>
                            <th onClick={() => onSort('sell', 'number')}>
                                Sold
                            </th>
                            <th onClick={() => onSort('stock', 'number')}>
                                Stock
                            </th>
                            <th onClick={() => onSort('stockValue', 'number')}>
                                Stock value
                            </th>
                            <th onClick={() => onSort('profit', 'number')}>
                                Profit
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {stocks.map((item) => (
                            <tr key={item.legoId}>
                                <td data-label='Image'>
                                    <LegoImage legoId={item.legoId} />
                                </td>
                                <td data-label='Lego ID'>{item.legoId}</td>
                                <td data-label='Name'>{item.displayName}</td>
                                <td data-label='For Sale'>{item.buy}</td>
                                <td data-label='Sold'>{item.sell}</td>
                                <td data-label='Stock'>{item.stock}</td>
                                <td data-label='Stock Value'>
                                    {formatCurrency(
                                        item.stockValue,
                                        displayCurrency,
                                    )}
                                </td>
                                <td data-label='Profit'>
                                    {formatCurrency(
                                        item.profit,
                                        displayCurrency,
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default StockRecords;
